import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddTaskIcon from '@mui/icons-material/AddTask';
import EventIcon from '@mui/icons-material/Event';
import NotesIcon from '@mui/icons-material/Notes';
import ScheduleIcon from '@mui/icons-material/Schedule';
import type { IMyIssueSummary } from '@pulse/shared';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  DateField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ICON_SIZE,
  LabelWithIcon,
  Stack,
  TextField,
} from '@pulse/ui';
import { useEffect, useState } from 'react';

import { blockNegativeHourKeys, cappedHoursChange, isValidHours } from '../../../lib/hours-input.util';
import { isEditableWorkLogDate } from '../../../lib/work-log-edit-window.util';
import { useAddIssueWorkLog } from '../../delivery/hooks/useIssues';

const QUICK_HOURS = [0.5, 1, 2, 4];
// Matches CreateIssueWorkLogDto's @MaxLength(5000) on description (apps/api/src/modules/presales/dto/create-issue-work-log.dto.ts).
const NOTE_MAX = 5000;

function todayIso(): string {
  return new Date().toLocaleDateString('en-CA');
}

interface LogTimeDialogProps {
  open: boolean;
  onClose: () => void;
  issues: IMyIssueSummary[];
  /** Pre-selects the task when opened from a row's own "Log time" icon; unset for the header's
   *  global button, which starts with no task chosen. */
  defaultIssueId?: string;
}

// The header's primary CTA and each row's "Log time" icon both open this — a fast, dedicated
// flow (task picker + big hour input + quick-pick chips) rather than routing through
// IssueDetailDialog's Work Log tab, which is built for reviewing a single issue in depth, not
// rapid cross-task time entry.
export function LogTimeDialog({ open, onClose, issues, defaultIssueId }: LogTimeDialogProps) {
  const [issueId, setIssueId] = useState(defaultIssueId ?? '');
  const [hours, setHours] = useState('');
  const [date, setDate] = useState(todayIso());
  const [dateFieldValid, setDateFieldValid] = useState(true);
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setIssueId(defaultIssueId ?? '');
      setHours('');
      setDate(todayIso());
      setDateFieldValid(true);
      setNote('');
      setFormError(null);
    }
  }, [open, defaultIssueId]);

  const selected = issues.find((i) => i.id === issueId);
  const workLogMutation = useAddIssueWorkLog();
  const isDateEditable = Boolean(date) && isEditableWorkLogDate(date);
  const noteOverLimit = note.length > NOTE_MAX;
  // The 24h-per-day budget spans the user's WHOLE day (across every task/dialog it can be
  // logged from), which this dialog has no reliable way to know in advance — enforced
  // server-side instead (DailyHoursWithinLimit in IssueService).
  const isValid = Boolean(selected) && isValidHours(hours) && Number(hours) > 0 && Boolean(date) && dateFieldValid && isDateEditable && !noteOverLimit;

  function handleSave() {
    if (!isValid || !selected) return;
    workLogMutation.mutate(
      {
        projectId: selected.projectId,
        issueId: selected.id,
        data: { hours: Number(hours), date, description: note.trim() || undefined },
      },
      {
        // HOME_KEYS.issues/timeSummary are invalidated centrally by useAddIssueWorkLog itself
        // (see invalidateWorkLogQueries in delivery/hooks/useIssues.ts).
        onSuccess: () => onClose(),
        onError: (err: Error) => setFormError(err.message),
      },
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <LabelWithIcon icon={<ScheduleIcon fontSize={ICON_SIZE.sm} />} label="Log Time" />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}

          <Autocomplete
            fullWidth
            options={issues}
            getOptionLabel={(i) => `${i.issueKey} — ${i.name}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={issues.find((i) => i.id === issueId) ?? null}
            onChange={(_, value) => setIssueId(value?.id ?? '')}
            renderInput={(params) => (
              <TextField {...params} label={<LabelWithIcon icon={<AddTaskIcon fontSize={ICON_SIZE.sm} />} label="Task" inline />} required />
            )}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label={<LabelWithIcon icon={<AccessTimeIcon fontSize={ICON_SIZE.sm} />} label="Hours" inline />}
              type="number"
              inputProps={{ min: 0, max: 24, step: 'any' }}
              value={hours}
              onChange={cappedHoursChange(setHours, 24)}
              onKeyDown={blockNegativeHourKeys}
              fullWidth
              autoFocus={Boolean(defaultIssueId)}
            />
            <DateField
              label={<LabelWithIcon icon={<EventIcon fontSize={ICON_SIZE.sm} />} label="Date" inline />}
              value={date}
              onChange={setDate}
              onValidityChange={setDateFieldValid}
              invalidMessage="Please enter a valid Date."
              fullWidth
              error={Boolean(date) && !isDateEditable}
              helperText={Boolean(date) && !isDateEditable ? 'Only current/previous week' : ' '}
            />
          </Stack>

          <Stack direction="row" spacing={1}>
            {QUICK_HOURS.map((h) => (
              <Chip
                key={h}
                label={`${h}h`}
                onClick={() => setHours(String(h))}
                variant={hours === String(h) ? 'filled' : 'outlined'}
                color={hours === String(h) ? 'secondary' : 'default'}
              />
            ))}
          </Stack>

          <TextField
            label={<LabelWithIcon icon={<NotesIcon fontSize={ICON_SIZE.sm} />} label="Note (optional)" inline />}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            rows={2}
            error={noteOverLimit}
            helperText={noteOverLimit ? `${note.length}/${NOTE_MAX} — ${note.length - NOTE_MAX} characters over the limit` : `${note.length}/${NOTE_MAX}`}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={workLogMutation.isPending}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!isValid || workLogMutation.isPending}>
          {workLogMutation.isPending ? 'Saving…' : 'Save Entry'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
